import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'

interface CalendarV2Request {
    fromDate: string
    toDate: string
    scopes: string[]
    work: string
}

export interface CalendarV2Response {
    availability?: any[]
    shifts?: any[]
    vacation?: any[]
    nationalHolidays?: any[]
    birthday?: any
    leaveOfAbsence?: any[]
    absenceData?: {
        workedDays: number
        isLogAllowed: boolean
    }
    [key: string]: any
}

interface CreateAvailabilityRequest {
    availableId: number
    fromDate: string
    toDate: string
    fromTime: string
    toTime: string
    note?: string
    includeWeekends: boolean
}

interface CreateAvailabilityResponse {
    statusCode: number
    success: boolean
    message: string
    data?: {
        count: number
        token: string
    }
}

interface CreateShiftRequest {
    jobId: number
    fromDate: string
    toDate: string
    fromTime: string
    toTime: string
    break?: string
    note?: string
    includeWeekends: boolean
}

interface WorkByIdResponse {
    statusCode: number
    success: boolean
    message: string
    data?: {
        job?: any
        work?: any
        files?: any[]
        childProject?: any
        workTrip?: any
    }
}

export const calendarApi = createApi({
    reducerPath: 'calendarApi',
    baseQuery: fetchBaseQuery({
        baseUrl: process.env.ENV === 'local' ? `${process.env.API_URL}/api` : `${window.location.origin}/api`,
        prepareHeaders: (headers) => {
            const token = document.getElementsByName('csrf-token')[0]?.getAttribute('content')
            if (token) headers.set('X-CSRF-TOKEN', token)
            return headers
        },
        credentials: process.env.ENV === 'local' ? 'include' : 'same-origin',
    }),
    endpoints: (builder) => ({
        getCalendarV2: builder.query<CalendarV2Response, CalendarV2Request>({
            query: ({ fromDate, toDate, scopes, work }) => {
                console.log('📤 Fetch calendarV2 with:', { fromDate, toDate, scopes })
                return {
                    url: '/employee/calendar/v2',
                    method: 'GET',
                    params: {
                        fromDate,
                        toDate,
                        scopes: scopes.join(','),
                        work: work,
                    },
                }
            },
            transformResponse: (response: any): CalendarV2Response => {
                console.log('📥 Raw response from API:', response)

                if (!response || typeof response !== 'object' || !response.data) {
                    console.warn('❗ Unexpected calendar response format:', response)
                    return {}
                }

                console.log('✅ Parsed calendar data:', response.data)
                return response.data as CalendarV2Response
            }
        }),

        createAvailability: builder.mutation<CreateAvailabilityResponse, CreateAvailabilityRequest>({
            query: (body) => ({
                url: '/employee/calendar/create/availability',
                method: 'POST',
                body,
                headers: {
                    'Content-Type': 'application/json',
                    'Response-V2': '1',
                },
            }),
        }),

        deleteAvailability: builder.mutation<
            { statusCode: number; success: boolean; message: string; data?: { deleted: number } },
            { ids: number[] }
        >({
            query: (body) => ({
                url: '/employee/calendar/delete/availability',
                method: 'DELETE',
                body,
                headers: {
                    'Content-Type': 'application/json',
                    'Response-V2': '1',
                },
            }),
        }),

        updateAvailability: builder.mutation<
            { statusCode: number; success: boolean; message: string; data?: any },
            { id: number; availableId: number; date: string; fromTime: string; toTime: string; note?: string }
        >({
            query: ({ id, ...body }) => ({
                url: `/employee/calendar/update/availability/${id}`,
                method: 'PUT',
                body,
                headers: {
                    'Content-Type': 'application/json',
                    'Response-V2': '1',
                },
            }),
        }),

        createShift: builder.mutation<CreateAvailabilityResponse, CreateShiftRequest>({
            query: (body) => ({
                url: '/employee/calendar/create/shift',
                method: 'POST',
                body,
                headers: {
                    'Content-Type': 'application/json',
                    'Response-V2': '1',
                },
            }),
        }),

        updateShift: builder.mutation<
            { statusCode: number; success: boolean; message: string; data?: any },
            { id: number; date: string; fromTime: string; toTime: string; break?: string; note?: string }
        >({
            query: ({ id, ...body }) => ({
                url: `/employee/calendar/update/shift/${id}`,
                method: 'PUT',
                body,
                headers: {
                    'Content-Type': 'application/json',
                    'Response-V2': '1',
                },
            }),
        }),

        deleteShift: builder.mutation<
            { statusCode: number; success: boolean; message: string; data?: { deleted: number } },
            { id: number }
        >({
            query: ({ id }) => ({
                url: `/employee/calendar/delete/shift/${id}`,
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json',
                    'Response-V2': '1',
                },
            }),
        }),
        getWorkById: builder.query<WorkByIdResponse, number>({
            query: (workHourId) => ({
                url: `/employee/work/${workHourId}`,
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                },
            }),
        }),

    }),
})

export const { useGetCalendarV2Query,
    useCreateAvailabilityMutation,
    useDeleteAvailabilityMutation,
    useUpdateAvailabilityMutation,
    useUpdateShiftMutation,
    useCreateShiftMutation,
    useDeleteShiftMutation,
    useGetWorkByIdQuery, } = calendarApi
